import { CardContent, Typography } from '@mui/material';
import type { RestoreInstanceVolumeInput } from '@repo/api-gw-sdk';
import { useState } from 'react';

import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { ContactSupportLink } from '@/components/support/support';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';
import { SingleVolumeConfig } from './singleVolumeConfig';

import { StepContainer } from '../../wizard/StepContainer';
import { type RestoreEc2InstanceState } from '../restoreEc2InstanceWizard';

export const VolumeConfiguration = (
  props: StepProps<RestoreEc2InstanceState>
) => {
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [errorCode, setErrorCode] = useState<number | undefined>(undefined);

  const startRestore = () => {
    setErrorCode(undefined);
    setIsRestoring(true);

    const volumeSettings: RestoreInstanceVolumeInput[] =
      props.wizardState.volumes.map((vol) => {
        const volConfigState = props.wizardState.volumesConfigState?.find(
          (volumeSetting) =>
            volumeSetting.providerVolumeId === vol.providerVolumeId
        );

        let volTags: RestoreInstanceVolumeInput['tags'] = {};
        if (volConfigState?.keepOriginalTags) {
          const volInitialConfigState =
            props.initialState.volumesConfigState?.find(
              (volumeSetting) =>
                volumeSetting.providerVolumeId === vol.providerVolumeId
            );
          volTags = volInitialConfigState?.tags || {};
        } else {
          volTags = volConfigState?.tags || {};
        }
        volTags['eon:restore'] = 'true';

        const currEncryption = props.wizardState.sharedEncryption?.isEncrypted
          ? props.wizardState.sharedEncryption
          : volConfigState?.encryption;
        const encryptionKey = getEncryptionKey(currEncryption);

        return {
          providerVolumeId: vol.providerVolumeId,
          tags: volTags,
          encryptionKeyId: encryptionKey,
          volumeEncryptionKeyId: encryptionKey!, // checking mandatory in the canGoNext func.
          volumeSettings: (volConfigState || vol).volumeSettings,
        };
      });

    const tags =
      (props.wizardState.keepOriginalTags
        ? props.initialState.tags
        : props.wizardState.tags) || {};

    return dal.restore.ec2
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          awsEc2: {
            region: props.wizardState.regionName!,
            instanceType: props.wizardState.instanceType!,
            subnetId: props.wizardState.AWSSubnetId!,
            securityGroupIds: props.wizardState.securityGroupIds,
            tags: { ...tags, 'eon:restore': 'true' },
            volumeRestoreParameters: volumeSettings,
            startInstanceAfterRestore:
              props.wizardState.startInstanceAfterRestore,
          },
        },
      })
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.resourceName}`
        )
      )
      .catch((error) => {
        setErrorCode(error.code);
        setIsRestoring(false);
      });
  };

  const canGoNext = () => {
    if (
      isRestoring ||
      !props.wizardState.instanceType ||
      !props.wizardState.AWSSubnetId ||
      !props.wizardState.regionName ||
      !props.wizardState.tags ||
      !props.wizardState.restoreAccount
    ) {
      return false;
    }

    let hasEncryptionKeyId = false;

    if (props.wizardState.sharedEncryption?.isEncrypted) {
      hasEncryptionKeyId = !!getEncryptionKey(
        props.wizardState.sharedEncryption
      );
    } else {
      hasEncryptionKeyId = props.wizardState.volumes?.every((vol) => {
        const currentVolEncryption = props.wizardState.volumesConfigState?.find(
          (volConf) => volConf.providerVolumeId === vol.providerVolumeId
        )?.encryption;
        return !!getEncryptionKey(currentVolEncryption);
      });
    }

    if (!hasEncryptionKeyId) {
      return false;
    }

    return true;
  };

  const getNavigationComponent = () => {
    if (errorCode) {
      return (
        <Typography color='error'>
          An error prevented the restore. Please <ContactSupportLink /> for
          help. Error code: {errorCode}
        </Typography>
      );
    }

    return <EonTagRemark />;
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={getNavigationComponent()}
      stepperLabels={props.stepperLabels}
      stepperIndex={2}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={startRestore}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <CardContent className='p-0'>
        {props.wizardState.regionName && props.wizardState.restoreAccount && (
          <EncryptionComponent
            title='Encrypt all volumes with the same key'
            canBeTurnedOff={true}
            encryption={props.wizardState.sharedEncryption}
            accountId={props.wizardState.restoreAccount.id}
            regionName={props.wizardState.regionName}
            onChange={(encryption) =>
              props.setWizardState((state) => ({
                ...state,
                sharedEncryption: encryption,
              }))
            }
          />
        )}
      </CardContent>
      <CardContent className='p-0'>
        {props.wizardState.volumes?.map((vol) => {
          const volumeSettings = props.wizardState.volumesConfigState?.find(
            (v) => v.providerVolumeId === vol.providerVolumeId
          );
          const initialVolumeSettings =
            props.initialState.volumesConfigState?.find(
              (v) => v.providerVolumeId === vol.providerVolumeId
            );
          if (
            !initialVolumeSettings ||
            !volumeSettings ||
            !props.wizardState.regionName ||
            !props.wizardState.restoreAccount
          ) {
            return null;
          }
          return (
            <SingleVolumeConfig
              key={vol.providerVolumeId}
              vol={vol}
              initialSettings={initialVolumeSettings}
              volumeSettings={volumeSettings}
              setWizardState={props.setWizardState}
              restoreAccountId={props.wizardState.restoreAccount.id}
              regionName={props.wizardState.regionName}
              showEncryption={!props.wizardState.sharedEncryption?.isEncrypted}
            />
          );
        })}
      </CardContent>
    </StepContainer>
  );
};
